/**
 * Inline upload status — renders progress bars and error banners for
 * any in-flight or recently-failed uploads. Auto-clears each row a
 * couple of seconds after completion.
 */
import { AlertCircle, X } from 'lucide-react';

import { useFileExplorerStore } from '../../stores/file-explorer-store';

export function UploadStatusBar() {
  const uploads = useFileExplorerStore((s) => s.uploads);
  const clearUpload = useFileExplorerStore((s) => s.clearUpload);

  const entries = Object.entries(uploads);

  if (entries.length === 0) return null;

  return (
    <ul className="flex flex-col gap-1 border-t border-border px-2 py-1.5 text-[11px]">
      {entries.map(([id, status]) => {
        const pct =
          status.total > 0 ? Math.min(100, Math.round((status.loaded / status.total) * 100)) : 0;
        const done = status.loaded >= status.total && status.total > 0;
        return (
          <li key={id} className="flex items-center gap-2">
            {status.error ? (
              <AlertCircle className="h-3 w-3 shrink-0 text-error" aria-hidden />
            ) : null}
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{status.filename}</span>
            {!status.error && (
              <span className="shrink-0 tabular-nums text-muted-foreground/80">{pct}%</span>
            )}
            {(done || status.error) && (
              <button
                type="button"
                onClick={() => clearUpload(id)}
                aria-label="Dismiss"
                className="flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            )}
            <div
              aria-hidden
              className="absolute left-0 right-0 hidden h-0.5 bg-foreground/10"
              style={{ width: `${pct}%` }}
            />
          </li>
        );
      })}
    </ul>
  );
}
